import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ConfirmDeleteModal from '@/components/modal/common/ConfirmDeleteModal';
import HaggleEventFormModal from '@/components/modal/haggle-event/HaggleEventFormModal';
import LoadingSkeletonModalTable from '@/components/modal/prompt/LoadingSkeletonModalTable';
import filterSwitch from '@/components/panel/haggle-event/utils/filterSwitch';
import { IPaginationTable } from '@/components/table/common/interfaces/IPaginationTable';
import HaggleEventTable from '@/components/table/haggle-event/HaggleEventTable';
import Button from '@/components/ui/button';
import {
	DEFAULT_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
} from '@/components/utils/constants';
import { HaggleType } from '@/components/utils/enums';
import {
	calculateStatus,
	formatHaggleEventDate,
	formatPrice,
	getHaggleTypeDisplayText,
} from '@/components/utils/utils';
import { useHasStoreProducts } from '@/hooks/useHasStoreProducts';
import { useModal } from '@/hooks/useModal';
import { useUser } from '@/hooks/useUser';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { IHaggleEvent } from '@/interfaces/api/IHaggleEvent';
import { InventoryType } from '@/interfaces/api/InventoryType';
import { HaggleEventFormOptions } from '@/interfaces/api/haggle-event/HaggleEventFormOptions.enum';
import { ICollectionHaggleEvent } from '@/interfaces/api/haggle-event/ICollectionHaggleEvent';
import { IHaggleWithAction } from '@/interfaces/api/haggle-event/IHaggleWithAction';
import { UserRole } from '@/interfaces/api/user/UserRole.enum';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';

const HaggleEventDashboard = () => {
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	const [pageNumber, setPageNumber] = useState(1);
	const [isDeleteLoading, setIsDeleteLoading] = useState(false);
	const [haggleEventOption, setHaggleEventOption] =
		useState<HaggleEventFormOptions>(HaggleEventFormOptions.CREATE);
	const [haggleEventId, setHaggleEventId] = useState<number | undefined>(
		undefined,
	);
	const [selectedHaggleEventType, setSelectedHaggleEventType] =
		useState<HaggleType | null>(null);
	const [filterSelected, setFilterSelected] = useState('');
	const [searchQuery, setSearchQuery] = useState('');
	const navigate = useNavigate();
	const filter = {
		'page[size]': pageSize,
		'page[number]': pageNumber,
		...(searchQuery && { 'filter[id]': searchQuery }),
		...(filterSelected && filterSwitch(filterSelected)),
	};
	const [isBusinessEvent, setIsBusinessEvent] = useState<boolean>(false);
	const [isStripeConnected, setIsStripeConnected] = useState(false);
	const [checkingStripeStatus, setCheckingStripeStatus] = useState(true);

	// Check if Stripe is connected when component mounts
	useEffect(() => {
		const checkStripeStatus = async () => {
			try {
				const res = await haggleApiService.getStripeAccountStatus();
				setIsStripeConnected(res.chargesEnabled);
			} catch (err) {
				console.error('Failed to check Stripe account status:', err);
			} finally {
				setCheckingStripeStatus(false);
			}
		};

		checkStripeStatus();
	}, []);

	const {
		data: haggleEvents,
		refetch,
		isLoading: haggleEventIsLoading,
		isError: haggleEventIsError,
		error: haggleEventError,
	} = useQuery<ICollectionHaggleEvent>({
		queryKey: [ApiQueryKeys.getAllHaggleEvents, pageSize, pageNumber],
		queryFn: () =>
			haggleApiService.getAllHaggleEvents({
				params: filter,
			}),
		refetchOnWindowFocus: false,
	});

	const { hasProducts, isLoading: isCheckingProducts } =
		useHasStoreProducts(true);

	useEffect(() => {
		refetch();
	}, [filterSelected, refetch]);

	if (haggleEventIsError) {
		if (haggleEventError instanceof Error) {
			notificationService.error(haggleEventError.message);
		} else {
			notificationService.error('Unknown error fetching haggle events');
		}
	}

	const handlePaginationOptions = (
		collection: ICollectionHaggleEvent,
	): IPaginationTable => {
		return {
			pageSize,
			pageNumber,
			pageCount: collection.pageCount,
			itemCount: collection.itemCount,
			pageSizeOptions: PAGE_SIZE_OPTIONS,
			setPageSize,
			setPageNumber,
		};
	};

	const {
		modal: updateModal,
		openModal,
		closeModal,
	} = useModal({
		children: (
			<HaggleEventFormModal
				id={haggleEventId}
				closeAfterSubmit={() => {
					closeModal();
					if (haggleEventOption === HaggleEventFormOptions.UPDATE) refetch();
				}}
				defaultClose={() => closeModal()}
				haggleOption={haggleEventOption}
				isBusinessEvent={selectedHaggleEventType !== null}
			/>
		),
		modalBoxClassName: 'w-full max-w-3xl',
		closeOnOutClick: false,
	});

	const openUnifiedModal = (
		option: HaggleEventFormOptions,
		id: number | undefined = undefined,
	) => {
		setHaggleEventOption(option);
		setHaggleEventId(id);

		if (id && haggleEvents?.data) {
			const event = haggleEvents.data.find((event) => event.id === id);
			setSelectedHaggleEventType(event?.haggleType || null);
		} else {
			setSelectedHaggleEventType(null);
		}

		openModal();
	};

	const handleDeleteHaggle = async (id: number) => {
		setIsDeleteLoading(true);
		try {
			await haggleApiService.deleteHaggleEvent(id);

			notificationService.success('Haggle Event deleted successfully!');
		} catch (error) {
			if (error instanceof Error) {
				notificationService.error(error.message);
			} else {
				notificationService.error(
					`Unknown error when trying to delete a Haggle Event: ${error}`,
				);
			}
		} finally {
			setIsDeleteLoading(false);
			refetch();
		}
	};

	const columnHelper = createColumnHelper<IHaggleWithAction>();

	const columns = [
		columnHelper.accessor('id', {
			cell: (info) => <div className="w-[50px]">{info.getValue()}</div>,
			header: () => <div className="w-[50px]">ID</div>,
		}),
		{
			id: 'status',
			cell: (info: { row: { original: unknown } }) => {
				const haggleEvent = info.row.original as IHaggleEvent;
				return (
					<div className="w-[70px]">
						{calculateStatus(haggleEvent.endDate, haggleEvent.startDate)}
					</div>
				);
			},
			header: () => <div className="w-[70px]">STATUS</div>,
		},
		columnHelper.accessor('startDate', {
			cell: (info) => (
				<div className="w-[170px] text-center">
					{formatHaggleEventDate(
						info.row.original.startDate,
						info.row.original.endDate,
					)}
				</div>
			),
			header: () => <div className="w-[170px] text-center">DATE</div>,
		}),
		columnHelper.accessor('haggleType', {
			cell: (info) => {
				const displayType = getHaggleTypeDisplayText(info.getValue());

				return <div className="w-[100px] text-center">{displayType}</div>;
			},
			header: () => <div className="w-[100px] text-center">TYPE</div>,
		}),
		columnHelper.accessor('agreementsStats', {
			cell: (info) => {
				return (
					<div className="w-[120px] text-center">{`${info.getValue()}`}</div>
				);
			},
			header: () => <div className="w-[120px] text-center">DEALS/CHATS</div>,
		}),
		columnHelper.accessor('totalSalesMade', {
			cell: (info) => {
				const totalSalesMade = info.getValue()?.toFixed(2) ?? 'N/A';
				return (
					<div className="w-[120px] text-center">{`${formatPrice(
						totalSalesMade,
					)}`}</div>
				);
			},
			header: () => <div className="w-[120px] text-center">TOTAL SALES</div>,
		}),
		columnHelper.accessor('totalProfit', {
			cell: (info) => {
				const totalProfit = info.getValue()?.toFixed(2) ?? 'N/A';
				return (
					<div className="w-[200px] text-center">{`${formatPrice(
						totalProfit,
					)}`}</div>
				);
			},
			header: () => <div className="w-[200px] text-center">REVENUE UPLIFT</div>,
		}),
		columnHelper.accessor('action', {
			header: () => <div className="w-[135px] text-center">Action</div>,
			cell: (info) => (
				<div className="flex gap-2 w-[135px]">
					<button
						className="font-normal text-blue-500 hover:underline"
						onClick={() => {
							navigate(`/user/dashboard/haggle-events/${info.row.original.id}`);
						}}
						data-test={`view-button-${info.row.original.id}`}
					>
						View
					</button>
					<button
						className="font-normal text-blue-500 hover:underline"
						onClick={() => {
							openUnifiedModal(
								HaggleEventFormOptions.UPDATE,
								info.row.original.id,
							);
						}}
						data-test={`update-button-${info.row.original.id}`}
					>
						Edit
					</button>

					<button
						className="font-normal text-red-500 hover:underline"
						onClick={() => handleOpenDeleteModal(info.row.original.id)}
						disabled={isDeleteLoading}
						data-test={`delete-button-${info.row.original.id}`}
					>
						Delete
					</button>
				</div>
			),
		}),
	];

	const { user } = useUser();

	const isDiamondUser = user?.roles.includes(UserRole.MerchantDiamond);

	const {
		modal: haggleModal,
		openModal: openHaggleModal,
		closeModal: closeHaggleModal,
	} = useModal({
		children: (
			<HaggleEventFormModal
				defaultClose={() => closeHaggleModal()}
				refetchHaggleEvents={refetch}
				isBusinessEvent={isBusinessEvent}
			/>
		),
		closeOnOutClick: false,
		modalBoxClassName: 'w-full max-w-6xl',
	});

	const handleOpenDeleteModal = (id: number) => {
		setHaggleEventId(id);
		openConfirmDeleteModal();
	};

	const {
		modal: confirmDeleteModal,
		openModal: openConfirmDeleteModal,
		closeModal: closeConfirmDeleteModal,
	} = useModal({
		children: (
			<ConfirmDeleteModal
				submit={handleDeleteHaggle}
				closeModal={() => closeConfirmDeleteModal()}
				message={`Are you sure you want to delete the Haggle Event with ID ${haggleEventId}?`}
				elementId={haggleEventId}
			/>
		),
		closeOnOutClick: false,
		modalBoxClassName: 'max-w-[600px]',
	});

	return (
		<div className="bg-grey-5 min-h-content overflow-y-auto ">
			{haggleEventIsLoading && <LoadingSkeletonModalTable />}

			{haggleEvents && !haggleEventIsLoading && (
				<div className="flex flex-col h-full grow gap-4">
					{isDiamondUser ? (
						<div className="flex flex-col gap-2 sm:flex-row sm:gap-x-2">
							<Button
								color="white"
								className="border !border-grey-30 w-full sm:w-auto"
								shape="rounded"
								transition="none"
								onClick={() => {
									if (!hasProducts && !isCheckingProducts) {
										notificationService.error(
											'You need to have at least one product in your store to create a haggle event.',
										);
										return;
									}
									if (
										user?.inventoryType === InventoryType.CSV &&
										!isStripeConnected &&
										!checkingStripeStatus
									) {
										notificationService.error(
											'You need to add your bank account to receive payouts before creating haggle event',
										);
										return;
									}
									setIsBusinessEvent(false);
									openHaggleModal();
								}}
							>
								+ Create Haggle Event for Consumer
							</Button>
							<Button
								color="white"
								className="border !border-grey-30 w-full sm:w-auto"
								shape="rounded"
								transition="none"
								onClick={() => {
									if (!hasProducts && !isCheckingProducts) {
										notificationService.error(
											'You need to have at least one product in your store to create a haggle event.',
										);
										return;
									}
									if (
										user?.inventoryType === InventoryType.CSV &&
										!isStripeConnected &&
										!checkingStripeStatus
									) {
										notificationService.error(
											'You need to add your bank account to receive payouts before creating haggle event',
										);
										return;
									}
									setIsBusinessEvent(true);
									openHaggleModal();
								}}
							>
								+ Create Haggle Event for Business
							</Button>
						</div>
					) : (
						<Button
							color="white"
							className="border !border-grey-30 w-full sm:w-fit"
							shape="rounded"
							transition="none"
							onClick={() => {
								if (!hasProducts && !isCheckingProducts) {
									notificationService.error(
										'You need to have at least one product in your store to create a haggle event.',
									);
									return;
								}
								if (
									user?.inventoryType === InventoryType.CSV &&
									!isStripeConnected &&
									!checkingStripeStatus
								) {
									notificationService.error(
										'You need to add your bank account to receive payouts before creating haggle event',
									);
									return;
								}
								setIsBusinessEvent(false);
								openHaggleModal();
							}}
							data-test="new-haggle-modal-button"
						>
							+ Create Haggle Event
						</Button>
					)}

					<HaggleEventTable
						collection={haggleEvents}
						paginationOption={handlePaginationOptions(haggleEvents)}
						columns={columns}
						refetch={refetch}
						query={searchQuery}
						setQuery={setSearchQuery}
						setFilterSelected={setFilterSelected}
					/>
				</div>
			)}
			{updateModal}
			{haggleModal}
			{confirmDeleteModal}
		</div>
	);
};
export default HaggleEventDashboard;
